/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Icon, IconButton, Tooltip } from '@mui/material';
import { ChipStatus, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { IntegrationDTO, IntegrationParameterDTO, IntegrationSettingDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { dateDiff, formatDateTime } from '../../../shared/utils';

interface IIntegrationParameterMaintenanceProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const IntegrationParameterMaintenance: React.FC<IIntegrationParameterMaintenanceProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [integrationSettingList, setIntegrationSettingList] = useState<IntegrationSettingDTO[]>([]);
    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [integrationParameterDTO, setIntegrationParameterDTO] = useState<IntegrationParameterDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setIntegrationParameterDTO({
            ...integrationParameterDTO,
            id: '',
            integrationSettingId: parentId ?? '',
            typeId: '',
            key: '',
            value: ''
        } as IntegrationParameterDTO);

        if (isOpenMaintenance) {
            handleListGetAll(ListId.linksphere.integrationParameter.typeId, setTypeList);
            handleIntegrationSettingGetAll();
            handleUserGetAllAsync();
            handleGetIntegrationByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleIntegrationSettingGetAll = useCallback(async () => {
        const integrationSettingFilterDTO = {

        };
        GenericService.GetAllAsync1('linksphere.integrationSetting', integrationSettingFilterDTO, setLoading, setIntegrationSettingList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async () => {
        const userFilterDTO = {
            integrationId: userInfo.user.integrationId,
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetIntegrationByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('linksphere.integrationParameter', data.id, setLoading, setIntegrationParameterDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = integrationParameterDTO as IntegrationParameterDTO;

        if (integrationParameterDTO?.id === '' || integrationParameterDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('linksphere.integrationParameter', dto, setIntegrationParameterDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('linksphere.integrationParameter', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [integrationParameterDTO]);
    
    const handleChangeSecurity = useCallback(async (isSecret: boolean) => {
        const dto = integrationParameterDTO as IntegrationParameterDTO;
        dto.isSecret = !dto.isSecret;
        setIntegrationParameterDTO({...dto, isSecret: !isSecret} as IntegrationParameterDTO);

        dto.updatedById = userInfo.user.id;
        GenericService.UpdateAsync1('linksphere.integrationParameter', dto, setActionExecuted, setLoading, showMessage, false);

    }, [integrationParameterDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'close', text: 'Close', icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];
    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='INTEGRATION PARAMETER DETAILS'
                icon='grid_view_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='integrationSettingId'
                                label='Integration Setting'
                                placeholder='Integration Setting'
                                value={integrationParameterDTO?.integrationSettingId}
                                setValue={setIntegrationParameterDTO}
                                obj={integrationParameterDTO}
                                valueList={integrationSettingList}
                                required={true}
                                hasNoneSelect={false}
                                disabled={integrationParameterDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='typeId'
                                label='Type'
                                placeholder='Type'
                                value={integrationParameterDTO?.typeId}
                                setValue={setIntegrationParameterDTO}
                                obj={integrationParameterDTO}
                                valueList={typeList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextFieldInput
                                id='key'
                                label='Key'
                                value={integrationParameterDTO?.key}
                                setValue={setIntegrationParameterDTO}
                                obj={integrationParameterDTO}
                                required={true}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={5.5}>
                            <TextFieldInput
                                id='value'
                                label='Value'
                                value={integrationParameterDTO?.value}
                                setValue={setIntegrationParameterDTO}
                                obj={integrationParameterDTO}
                                required={true}
                                maxLength={36}
                                type={integrationParameterDTO?.isSecret ? 'password' : 'text'}
                            />
                        </Grid>
                        <Grid item xs={12} md={0.5}>
                            <Tooltip title={'Change Variable type to secret'}>
                                <IconButton
                                    id={'isSecret'}
                                    size='small'
                                    onClick={() => handleChangeSecurity(integrationParameterDTO?.isSecret ?? false)}
                                    
                                >
                                    <Icon fontSize='small' color={'info'}>{'lock_outlined'}</Icon>
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        {integrationParameterDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={integrationParameterDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === integrationParameterDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(integrationParameterDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === integrationParameterDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(integrationParameterDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};